<template>
  <div class="w-full">
    <Head title="Tekniker &amp; verktyg" />
    <!-- Hero section -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16">
      <div class="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
        <!-- Left: heading + CTA -->
        <div class="flex-1 flex flex-col gap-5 md:gap-6">
          <h1
            class="font-[Poppins] m-0 leading-[1.2] tracking-[-2px]"
            style="font-size: clamp(36px, 5vw, 60px); color: #191c1e"
          >
            Lär dig<br />teknikerna<br />
            <span class="text-teal-500">bakom maten</span>
          </h1>
          <p class="text-[18px] text-[#3c4947] leading-[30px] max-w-[576px]">
            Jag tror på att förstå hantverket. Här delar jag med mig av de små
            knepen och stora teknikerna som förvandlar vardagsmat till något
            alldeles extra.
          </p>
        </div>

        <!-- Right: hero image -->
        <div class="flex-1 relative w-full hidden md:block">
          <div
            class="overflow-hidden rounded-3xl"
            style="
              height: clamp(220px, 40vw, 400px);
              box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
            "
          >
            <!-- Show first published technique's hero image, or a teal gradient -->
            <img
              src="@/images/tools-hero.jpg"
              alt="Tekniker & verktyg"
              class="w-full h-full object-cover"
            />
          </div>
          <!-- Floating card: desktop only -->
          <div
            class="hidden md:flex absolute bottom-[-20px] left-[-20px] items-center gap-3 px-5 py-4 rounded-2xl bg-white"
            style="
              box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
              max-width: 240px;
            "
          >
            <div
              class="shrink-0 flex items-center justify-center rounded-xl bg-teal-50"
              style="width: 40px; height: 40px"
            >
              <Lightbulb :size="20" class="text-teal-500" />
            </div>
            <div>
              <p
                class="font-[Poppins] font-bold text-[13px] text-[#191c1e] m-0 mb-0.5"
              >
                Take-aways
              </p>
              <p class="text-[11px] text-[#6c7a77]">
                Massor av tips i varje artikel
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured techniques: first two as big cards -->
    <section
      v-if="techniques.length"
      id="tekniker"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Link
          v-for="technique in techniques.slice(0, 2)"
          :key="technique.id"
          :href="technique.path"
          class="relative overflow-hidden rounded-3xl block no-underline"
          style="height: clamp(260px, 40vw, 440px)"
        >
          <img
            v-if="technique.heroImageUrl"
            :src="technique.heroImageUrl"
            :alt="technique.title"
            class="w-full h-full object-cover"
            loading="lazy"
          />
          <div
            v-else
            class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
          />
          <div
            class="absolute inset-0 flex flex-col justify-end p-6 md:p-8"
            style="
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.85) 0%,
                rgba(0, 0, 0, 0.2) 55%,
                rgba(0, 0, 0, 0) 100%
              );
            "
          >
            <span
              class="inline-block px-3 py-1 rounded-full mb-3 text-[10px] font-bold tracking-widest uppercase w-fit"
              :style="{ backgroundColor: technique.badgeColor, color: '#fff' }"
              >{{ technique.badge }}</span
            >
            <h2
              class="font-[Poppins] font-bold text-white m-0 mb-2"
              style="font-size: clamp(22px, 3vw, 28px)"
            >
              {{ technique.title }}
            </h2>
            <p
              class="text-white/85 text-sm leading-5 mb-5 max-w-xs m-0 mb-5 line-clamp-3"
            >
              {{ technique.intro }}
            </p>
            <span
              class="inline-flex items-center gap-2 text-teal-400 font-bold text-sm"
            >
              Visa steg för steg <ArrowRight :size="14" />
            </span>
          </div>
        </Link>
      </div>
    </section>

    <!-- Tools section -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16">
      <div class="mb-6 md:mb-8">
        <h2
          class="font-[Poppins] font-bold text-[#191c1e] m-0"
          style="font-size: clamp(20px, 3vw, 24px)"
        >
          Verktyg
        </h2>
        <div class="h-0.5 mt-2 w-10 bg-teal-500" />
      </div>
      <Link
        :href="doughCalculatorPath"
        class="flex flex-col gap-3 p-4 sm:p-5 md:p-6 rounded-2xl md:rounded-3xl hover:opacity-90 transition-opacity border border-[#eceef0] bg-white no-underline"
      >
        <!-- Top row: icon + badge + title + arrow -->
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center rounded-2xl shrink-0 bg-teal-500/10 w-12 h-12 sm:w-14 sm:h-14"
          >
            <span class="text-[24px] sm:text-[28px]">⚖️</span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-block px-2.5 py-0.5 rounded-full bg-teal-500 text-white text-[10px] font-bold tracking-[0.8px]"
                >VERKTYG</span
              >
            </div>
            <h3
              class="font-[Poppins] font-bold text-[16px] sm:text-[17px] text-[#191c1e] m-0 leading-tight"
            >
              Degkalkylator
            </h3>
          </div>
          <ArrowRight :size="18" class="text-teal-500 shrink-0" />
        </div>
        <!-- Description row -->
        <p class="text-sm text-[#6c7a77] leading-5 m-0">
          Beräkna rätt mängder för jäst-deg, surdeg och berikad deg med
          bagarprocent. Stöd för flera satser och jästtyper.
        </p>
      </Link>
    </section>

    <!-- More techniques grid -->
    <section
      v-if="techniques.length > 2"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16"
    >
      <div class="flex items-center justify-between mb-6 md:mb-8">
        <div>
          <h2
            class="font-[Poppins] font-bold text-[#191c1e] m-0"
            style="font-size: clamp(20px, 3vw, 24px)"
          >
            Fler tekniktips
          </h2>
          <div class="h-0.5 mt-2 w-10 bg-teal-500" />
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
      >
        <Link
          v-for="technique in techniques.slice(2)"
          :key="technique.id"
          :href="technique.path"
          class="group rounded-2xl overflow-hidden border border-[#eceef0] bg-white hover:shadow-md transition-shadow no-underline"
        >
          <div class="overflow-hidden" style="height: 180px">
            <img
              v-if="technique.heroImageUrl"
              :src="technique.heroImageUrl"
              :alt="technique.title"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <div
              v-else
              class="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600"
            />
          </div>
          <div class="p-4">
            <span
              class="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2"
              :style="{ backgroundColor: technique.badgeColor, color: '#fff' }"
              >{{ technique.badge }}</span
            >
            <h3
              class="font-[Poppins] font-bold text-[15px] text-[#191c1e] mb-1"
            >
              {{ technique.title }}
            </h3>
            <p class="text-xs text-[#6c7a77] leading-5 line-clamp-2">
              {{ technique.intro }}
            </p>
            <p class="text-xs text-[#9ca3af] mt-2">{{ technique.readTime }}</p>
          </div>
        </Link>
      </div>
    </section>

    <!-- Empty state -->
    <div
      v-if="!techniques.length"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-24 text-center"
    >
      <p class="text-[#6c7a77] text-base">
        Inga tekniker publicerade ännu. Kom tillbaka snart!
      </p>
    </div>
  </div>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import { ArrowRight, Lightbulb } from "lucide-vue-next";

defineProps({
  techniques: { type: Array, default: () => [] },
  doughCalculatorPath: { type: String, required: true },
});
</script>
